import { useState, useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle, XCircle, Loader2, Search, ArrowRight, ArrowLeft, Banknote, Link2, Check, ChevronDown, Users, Sparkles, Ban, Layers, CheckCheck } from "lucide-react";
import { bankStatementAPI, clientAPI } from "@/lib/api";
import { toast } from "sonner";

function formatEur(v) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);
}

function round2(n) { return Math.round(n * 100) / 100; }

function getBankRowClassName(assigned, isAlready, isSuggested, isMultiSuggested) {
  if (assigned) return "bg-emerald-50/30";
  if (isAlready) return "bg-slate-100/60 opacity-60";
  if (isSuggested || isMultiSuggested) return "bg-indigo-50/30";
  return "bg-amber-50/20";
}

function BankRowIcon({ assigned, isAlready, isMultiSuggested, isSuggested }) {
  if (assigned) return <CheckCircle size={13} className="text-emerald-500" />;
  if (isAlready) return <Ban size={13} className="text-slate-400" />;
  if (isMultiSuggested) return <Layers size={13} className="text-purple-400" />;
  if (isSuggested) return <Sparkles size={13} className="text-indigo-400" />;
  return <XCircle size={13} className="text-slate-300" />;
}

const MODES = [
  { value: "virement", label: "Virement" },
  { value: "cheque", label: "Chèque" },
  { value: "carte", label: "CB" },
  { value: "especes", label: "Espèces" },
  { value: "prélèvement", label: "Prélèv." },
  { value: "traite", label: "Traite" },
];

function ManualAssignDialog({ open, transaction, onAssign, onMultiAssign, onClose }) {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  // Multi-selection support: pick several invoices, the transaction
  // amount is auto-distributed in date order (oldest first) up to each
  // invoice's `remaining` balance.
  const [selected, setSelected] = useState({}); // { docId: docObj }

  useEffect(() => { if (open) { setSearch(""); setResults([]); setSelected({}); } }, [open]);

  const doSearch = async (q) => {
    setSearch(q);
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    try { const res = await bankStatementAPI.searchInvoices(q); setResults(res.data || []); }
    catch { setResults([]); toast.error("Erreur recherche facture"); }
    finally { setLoading(false); }
  };

  const toggle = (doc) => {
    setSelected(prev => {
      const n = { ...prev };
      if (n[doc.id]) delete n[doc.id];
      else n[doc.id] = doc;
      return n;
    });
  };

  // Compute auto allocation in date order (oldest first) up to txn amount.
  // Returns array of {document_id, document_number, client_name, client_id, amount}
  const allocations = (() => {
    const docs = Object.values(selected);
    if (!transaction || docs.length === 0) return [];
    const sorted = [...docs].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    let left = round2(transaction.amount);
    const out = [];
    for (const d of sorted) {
      if (left <= 0) { out.push({ document_id: d.id, document_number: d.number, client_name: d.client_name, client_id: d.client_id, amount: 0 }); continue; }
      const take = round2(Math.min(left, d.remaining || 0));
      out.push({ document_id: d.id, document_number: d.number, client_name: d.client_name, client_id: d.client_id, amount: take });
      left = round2(left - take);
    }
    return out;
  })();
  const allocated = round2(allocations.reduce((s, a) => s + a.amount, 0));
  const remaining = round2((transaction?.amount || 0) - allocated);

  const confirm = () => {
    if (allocations.length === 0) return;
    if (allocations.length === 1) {
      const a = allocations[0];
      onAssign({ document_id: a.document_id, document_number: a.document_number, client_name: a.client_name, client_id: a.client_id });
      return;
    }
    // 2+ invoices: emit multi-allocation
    const filtered = allocations.filter(a => a.amount > 0);
    if (filtered.length === 0) { toast.error("Le montant du virement est nul"); return; }
    onMultiAssign(filtered);
  };

  const selectedCount = Object.keys(selected).length;
  let remainingClass = "text-emerald-600";
  if (remaining > 0.01) remainingClass = "text-amber-600";
  if (remaining < -0.01) remainingClass = "text-red-600";
  let remainingLabel = "Solde OK";
  if (remaining > 0.01) remainingLabel = `Reste ${formatEur(remaining)}`;
  if (remaining < -0.01) remainingLabel = `Dépassé de ${formatEur(-remaining)}`;

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="manual-assign-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm">Affecter à une ou plusieurs factures</DialogTitle>
        </DialogHeader>
        {transaction && (
          <div className="p-2 bg-slate-50 rounded text-[11px] mb-2">
            <p className="font-medium">{transaction.label}</p>
            <p className="text-slate-500">{transaction.date} — {formatEur(transaction.amount)}</p>
          </div>
        )}
        <p className="text-[10px] text-slate-400 mb-1">
          Cochez plusieurs factures pour répartir automatiquement le montant (facture la plus ancienne d&apos;abord).
        </p>
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Numéro de facture ou nom client…" value={search} onChange={e => doSearch(e.target.value)} className="pl-8 h-8 text-xs" data-testid="manual-search" />
        </div>
        <div className="space-y-0.5 mt-1 max-h-[300px] overflow-y-auto">
          {loading && <div className="text-center py-3"><Loader2 className="animate-spin h-4 w-4 text-slate-400 mx-auto" /></div>}
          {!loading && results.length === 0 && search.length >= 2 && <p className="text-[11px] text-slate-400 text-center py-3">Aucune facture impayée trouvée</p>}
          {results.map(d => {
            const isChecked = !!selected[d.id];
            const alloc = allocations.find(a => a.document_id === d.id);
            return (
              <button key={d.id} onClick={() => toggle(d)}
                className={`w-full flex items-center justify-between p-2 rounded transition-colors text-left ${isChecked ? "bg-indigo-50 border border-indigo-200" : "hover:bg-slate-50 border border-transparent"}`}
                data-testid={`assign-doc-${d.id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <input type="checkbox" checked={isChecked} readOnly className="accent-indigo-600 cursor-pointer" data-testid={`assign-check-${d.id}`} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-1">
                      <span className="font-mono text-[11px] font-medium text-indigo-600">{d.number}</span>
                      {d.date && <span className="text-[10px] text-slate-400">· {d.date}</span>}
                    </div>
                    <p className="text-[11px] text-slate-500 truncate">{d.client_name}</p>
                  </div>
                </div>
                <div className="text-right whitespace-nowrap pl-2">
                  <span className="text-[11px] font-medium">{formatEur(d.total_ttc)}</span>
                  <span className="text-[10px] text-slate-400 ml-1">reste {formatEur(d.remaining)}</span>
                  {isChecked && alloc && alloc.amount > 0 && (
                    <div className="text-[10px] text-indigo-600 font-medium">→ {formatEur(alloc.amount)}</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
        {selectedCount > 0 && transaction && (
          <div className="mt-2 p-2 bg-slate-50 rounded space-y-1">
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-slate-500">{selectedCount} facture(s) sélectionnée(s)</span>
              <span className={`font-semibold ${remainingClass}`} data-testid="multi-remaining-label">{remainingLabel}</span>
            </div>
            <div className="text-[11px]">
              <span className="text-slate-500">Affecté : </span>
              <span className="font-semibold" data-testid="multi-allocated">{formatEur(allocated)}</span>
              <span className="text-slate-400 mx-1">/</span>
              <span>{formatEur(transaction.amount)}</span>
            </div>
            <Button onClick={confirm} className="w-full mt-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-8" data-testid="multi-confirm-btn">
              <Check size={13} className="mr-1" />
              {selectedCount === 1 ? "Affecter à 1 facture" : `Affecter à ${selectedCount} factures`}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ClientAllocDialog({ open, transaction, onAllocate, onClose }) {
  const [clientSearch, setClientSearch] = useState("");
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [allocations, setAllocations] = useState({});
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  // When the dialog opens, reset all state. If the transaction has a
  // matched_client_id (detected by the backend from the bank label), or a
  // best_match with a client_id, pre-select that client and load their
  // invoices immediately so the user doesn't have to search.
  useEffect(() => {
    if (!open) return;
    setClientSearch(""); setClients([]); setSelectedClient(null); setInvoices([]); setAllocations({});
    const preClientId = transaction?.matched_client_id || transaction?.best_match?.client_id;
    if (preClientId) {
      (async () => {
        try {
          const res = await clientAPI.get(preClientId);
          const client = res.data;
          if (!client?.id) return;
          setSelectedClient(client);
          setLoadingInvoices(true);
          try { const inv = await bankStatementAPI.clientUnpaid(client.id); setInvoices(inv.data || []); }
          finally { setLoadingInvoices(false); }
        } catch { /* fallback: user searches manually */ }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // While the dialog is closing (`open=false` but the component is still
  // mounted during animation) `transaction` becomes null. Guard every read.
  const txAmount = transaction?.amount || 0;
  const remaining = txAmount - Object.values(allocations).reduce((s, v) => s + (parseFloat(v) || 0), 0);

  const searchClients = async (q) => {
    setClientSearch(q);
    if (q.length < 2) { setClients([]); return; }
    setLoadingClients(true);
    try { const res = await clientAPI.list({ search: q, limit: 20 }); setClients(res.data?.items || res.data || []); }
    catch (e) { setClients([]); toast.error("Erreur recherche client"); }
    finally { setLoadingClients(false); }
  };
  const selectClient = async (client) => {
    setSelectedClient(client); setAllocations({}); setLoadingInvoices(true);
    try { const res = await bankStatementAPI.clientUnpaid(client.id); setInvoices(res.data || []); }
    catch { setInvoices([]); } finally { setLoadingInvoices(false); }
  };
  const setAlloc = (docId, val) => {
    setAllocations(prev => { const n = { ...prev }; if (!val || parseFloat(val) === 0) delete n[docId]; else n[docId] = val; return n; });
  };
  const autoFill = () => {
    let left = txAmount;
    const newAlloc = {};
    for (const inv of invoices) { if (left <= 0) break; const take = Math.min(left, inv.remaining); newAlloc[inv.id] = take.toFixed(2); left = round2(left - take); }
    setAllocations(newAlloc);
  };
  const confirm = () => {
    const items = [];
    const clientName = selectedClient?.company || selectedClient?.nom_etablissement || "";
    for (const inv of invoices) { const amt = parseFloat(allocations[inv.id]); if (amt > 0) items.push({ document_id: inv.id, document_number: inv.number, client_name: clientName, client_id: selectedClient?.id, amount: round2(amt) }); }
    if (items.length === 0) { toast.error("Saisissez au moins un montant"); return; }
    onAllocate(items);
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { onClose(); setSelectedClient(null); setInvoices([]); setAllocations({}); setClientSearch(""); } }}>
      <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto" data-testid="client-alloc-dialog">
        <DialogHeader><DialogTitle className="text-sm flex items-center gap-2"><Users size={15} className="text-indigo-600" /> Point règlements par client</DialogTitle></DialogHeader>
        {transaction && (
          <div className="p-2 bg-indigo-50 rounded text-[11px] mb-2">
            <p className="font-medium">{transaction.label}</p>
            <p className="text-indigo-700 font-semibold">Montant a affecter : {formatEur(transaction.amount)}</p>
          </div>
        )}
        {!selectedClient ? (
          <div>
            <div className="relative mb-2">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Rechercher un client..." value={clientSearch} onChange={e => searchClients(e.target.value)} className="pl-8 h-8 text-xs" data-testid="client-search" />
            </div>
            <div className="space-y-0.5 max-h-[250px] overflow-y-auto">
              {loadingClients && <div className="text-center py-3"><Loader2 className="animate-spin h-4 w-4 text-slate-400 mx-auto" /></div>}
              {clients.map(c => (
                <button key={c.id} onClick={() => selectClient(c)} className="w-full flex items-center gap-2 p-2 hover:bg-indigo-50 rounded transition-colors text-left" data-testid={`select-client-${c.id}`}>
                  <Users size={12} className="text-slate-400" />
                  <span className="text-xs font-medium">{c.company || c.nom_etablissement || "-"}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" className="h-6 px-1 text-[10px]" onClick={() => { setSelectedClient(null); setInvoices([]); setAllocations({}); }}><ArrowLeft size={11} className="mr-0.5" /> Retour</Button>
                <span className="text-xs font-semibold text-slate-800">{selectedClient.company || selectedClient.nom_etablissement}</span>
              </div>
              <Button variant="outline" size="sm" className="h-6 px-2 text-[10px]" onClick={autoFill} data-testid="auto-fill-btn">Remplir auto</Button>
            </div>
            {loadingInvoices ? (
              <div className="text-center py-4"><Loader2 className="animate-spin h-4 w-4 text-slate-400 mx-auto" /></div>
            ) : invoices.length === 0 ? (
              <p className="text-[11px] text-slate-400 text-center py-4">Aucune facture impayee pour ce client</p>
            ) : (
              <>
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead className="text-[10px] uppercase px-2">Facture</TableHead>
                    <TableHead className="text-[10px] uppercase px-2 text-right">TTC</TableHead>
                    <TableHead className="text-[10px] uppercase px-2 text-right">Reste du</TableHead>
                    <TableHead className="text-[10px] uppercase px-2 w-28">Montant a affecter</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {invoices.map(inv => (
                      <TableRow key={inv.id} data-testid={`alloc-row-${inv.id}`}>
                        <TableCell className="text-[11px] px-2 font-mono">{inv.number}</TableCell>
                        <TableCell className="text-[11px] px-2 text-right">{formatEur(inv.total_ttc)}</TableCell>
                        <TableCell className="text-[11px] px-2 text-right font-medium text-red-600">{formatEur(inv.remaining)}</TableCell>
                        <TableCell className="px-2">
                          <Input type="number" step="0.01" min="0" max={inv.remaining} value={allocations[inv.id] || ""} onChange={e => setAlloc(inv.id, e.target.value)} placeholder="0.00" className="h-6 text-[11px] w-24" data-testid={`alloc-amount-${inv.id}`} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="flex items-center justify-between mt-2 p-2 bg-slate-50 rounded">
                  <div className="text-[11px]"><span className="text-slate-500">Affecte : </span><span className="font-semibold">{formatEur(txAmount - remaining)}</span><span className="text-slate-400 mx-1">/</span><span>{formatEur(txAmount)}</span></div>
                  <span className={`text-[11px] font-semibold ${(() => { if (remaining > 0.01) return "text-amber-600"; if (remaining < -0.01) return "text-red-600"; return "text-emerald-600"; })()}`}>
                    {(() => { if (remaining > 0.01) return `Reste ${formatEur(remaining)}`; if (remaining < -0.01) return `Depasse de ${formatEur(-remaining)}`; return "Solde OK"; })()}
                  </span>
                </div>
                <Button onClick={confirm} className="w-full mt-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-8" disabled={Object.keys(allocations).length === 0} data-testid="confirm-alloc-btn">
                  <Check size={13} className="mr-1" /> Confirmer l&apos;affectation
                </Button>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function MatchStep({ transactions, filename, fileHash = "", storagePath = "", debits = [], source = "banque", onDone, onCancel, initialDraft = null }) {
  // A draft may exist with results=[] (auto-saved before /match completed);
  // treat empty array as "needs re-matching" so the page never appears blank.
  const hasInitialResults = Array.isArray(initialDraft?.results) && initialDraft.results.length > 0;
  const [results, setResults] = useState(hasInitialResults ? initialDraft.results : null);
  const [loading, setLoading] = useState(!hasInitialResults);
  const [validating, setValidating] = useState(false);
  const [assignments, setAssignments] = useState(initialDraft?.assignments || {});
  const [manualDialog, setManualDialog] = useState(null);
  const [clientDialog, setClientDialog] = useState(null);
  // Auto-save draft state
  const draftIdRef = useRef(initialDraft?.id || null);
  const lastSavedRef = useRef("");
  const saveTimerRef = useRef(null);

  useEffect(() => {
    // Skip re-fetching matches only when resuming with non-empty cached results.
    if (hasInitialResults) {
      setResults(initialDraft.results);
      setLoading(false);
      return;
    }
    if (!transactions || transactions.length === 0) {
      // Nothing to match → render empty state without spinning forever
      setResults([]);
      setLoading(false);
      return;
    }
    bankStatementAPI.match(transactions)
      .then(res => {
        setResults(res.data.results);
        const s = res.data.suggested || 0;
        const m = res.data.multi_suggested || 0;
        const a = res.data.already_processed || 0;
        toast.success(`${s + m} suggestion(s), ${a} déjà traité(s) sur ${res.data.total} lignes`);
      })
      .catch(e => toast.error(e.response?.data?.detail || "Erreur matching"))
      .finally(() => setLoading(false));
  }, [transactions, initialDraft, hasInitialResults]);

  // Auto-save the draft 2s after the user stops modifying assignments
  // so they can leave the page and resume later from the Historique tab.
  useEffect(() => {
    if (loading || !results) return;
    const payload = {
      id: draftIdRef.current,
      filename: filename || "",
      source,
      file_hash: fileHash,
      storage_path: storagePath,
      transactions,
      debits,
      results,
      assignments,
      total_lines: results.length,
      total_amount: round2(transactions.reduce((s, t) => s + (t.amount || 0), 0)),
    };
    const serialized = JSON.stringify({
      a: payload.assignments,
      f: payload.filename,
      n: payload.total_lines,
      // BUG 3 V2 P0 hotfix : include results' statuses so that a click on
      // "Déjà traité" (which mutates results[idx].status = "manually_skipped"
      // without touching assignments) actually triggers the auto-save.
      s: (results || []).map(r => `${r.index}:${r.status}`).join("|"),
    });
    if (serialized === lastSavedRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      bankStatementAPI.upsertDraft(payload)
        .then(res => { draftIdRef.current = res.data.id; lastSavedRef.current = serialized; })
        .catch(() => { /* silent — auto-save is best-effort */ });
    }, 600);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [assignments, results, loading, filename, fileHash, storagePath, transactions, debits]);

  const removeAssignment = (idx) => { setAssignments(prev => { const n = { ...prev }; delete n[idx]; return n; }); };

  // BUG 3 V2 (P0 hotfix CAF) — Mark a transaction as already treated manually
  // (outside KomptAI), so it disappears from the wizard without creating any
  // payment in DB. The line stays in the draft history with status
  // "manually_skipped" so a subsequent /validate is idempotent.
  // Saves the draft SYNCHRONOUSLY (no debounce wait) so the history counter
  // updates immediately for the user.
  const markManuallyTreated = (idx) => {
    const nextResults = results.map(r => r.index === idx ? { ...r, status: "manually_skipped" } : r);
    const nextAssignments = { ...assignments };
    delete nextAssignments[idx];
    setResults(nextResults);
    setAssignments(nextAssignments);
    // Immediate persistence (cancel any pending debounce)
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    const payload = {
      id: draftIdRef.current,
      filename: filename || "",
      source,
      file_hash: fileHash,
      storage_path: storagePath,
      transactions,
      debits,
      results: nextResults,
      assignments: nextAssignments,
      total_lines: nextResults.length,
      total_amount: round2(transactions.reduce((s, t) => s + (t.amount || 0), 0)),
    };
    bankStatementAPI.upsertDraft(payload)
      .then(res => {
        draftIdRef.current = res.data.id;
        lastSavedRef.current = JSON.stringify({
          a: payload.assignments, f: payload.filename, n: payload.total_lines,
          s: nextResults.map(r => `${r.index}:${r.status}`).join("|"),
        });
        toast.success("Ligne marquée comme déjà traitée");
      })
      .catch(() => toast.error("Erreur lors de la sauvegarde — réessayez"));
  };

  const acceptSuggestion = (idx) => {
    const r = results[idx];
    if (!r?.best_match) return;
    setAssignments(prev => ({ ...prev, [idx]: { document_id: r.best_match.document_id, document_number: r.best_match.document_number, client_name: r.best_match.client_name, client_id: r.best_match.client_id, amount: r.amount, date: r.date, mode: "virement" } }));
  };

  const acceptMultiSuggestion = (idx) => {
    const r = results[idx];
    if (!r?.multi_invoices) return;
    const items = r.multi_invoices.map(inv => ({ document_id: inv.document_id, document_number: inv.document_number, client_name: inv.client_name, client_id: inv.client_id, amount: inv.remaining }));
    setAssignments(prev => ({ ...prev, [idx]: { multi: true, items, client_name: items[0]?.client_name || "", amount: r.amount, date: r.date, mode: "virement" } }));
  };

  const acceptAllSuggestions = () => {
    if (!results) return;
    const newAssignments = { ...assignments };
    for (const r of results) {
      if (newAssignments[r.index]) continue;
      if (r.status === "suggested" && r.best_match) {
        newAssignments[r.index] = { document_id: r.best_match.document_id, document_number: r.best_match.document_number, client_name: r.best_match.client_name, client_id: r.best_match.client_id, amount: r.amount, date: r.date, mode: "virement" };
      } else if (r.status === "multi_suggested" && r.multi_invoices) {
        const items = r.multi_invoices.map(inv => ({ document_id: inv.document_id, document_number: inv.document_number, client_name: inv.client_name, client_id: inv.client_id, amount: inv.remaining }));
        newAssignments[r.index] = { multi: true, items, client_name: items[0]?.client_name || "", amount: r.amount, date: r.date, mode: "virement" };
      }
    }
    setAssignments(newAssignments);
    toast.success("Toutes les suggestions validées");
  };

  const assignToDoc = (idx, doc) => {
    const r = results[idx];
    setAssignments(prev => ({ ...prev, [idx]: { document_id: doc.document_id || doc.id, document_number: doc.document_number || doc.number, client_name: doc.client_name || "", client_id: doc.client_id || "", amount: r.amount, date: r.date, mode: "virement" } }));
  };

  const assignMultiple = (idx, invoiceAssignments) => {
    const r = results[idx];
    setAssignments(prev => ({ ...prev, [idx]: { multi: true, items: invoiceAssignments, client_name: invoiceAssignments[0]?.client_name || "", amount: r.amount, date: r.date, mode: "virement" } }));
  };

  const setMode = (idx, mode) => { setAssignments(prev => ({ ...prev, [idx]: { ...prev[idx], mode } })); };

  const validate = async () => {
    const toValidate = [];
    const aliasesToSave = [];
    for (const [idx, a] of Object.entries(assignments)) {
      const r = results[parseInt(idx)];
      const txLabel = (r?.label || "").trim();
      if (a.multi && a.items) {
        for (const item of a.items) toValidate.push({ document_id: item.document_id, amount: item.amount, date: a.date, mode: a.mode, comment: "Import relevé bancaire" });
        if (r?.status !== "suggested" && txLabel && a.items[0]?.client_id) aliasesToSave.push({ label_pattern: txLabel, client_id: a.items[0].client_id });
      } else if (a.document_id) {
        toValidate.push({ document_id: a.document_id, amount: a.amount, date: a.date, mode: a.mode, comment: "Import relevé bancaire" });
        if (r?.status !== "suggested" && txLabel && a.client_id) aliasesToSave.push({ label_pattern: txLabel, client_id: a.client_id });
      }
    }
    // BUG UX A : allow finalizing a draft where every line is already handled
    // (deja_traite / manually_skipped / manual assignment) with NO new payment
    // to create. We still need to call the backend so it can close the draft
    // and update the statement history record.
    const pendingAffectations = results.filter(
      r => (r.status === "unmatched" || r.status === "suggested" || r.status === "multi_suggested")
        && !assignments[r.index]
    ).length;
    if (toValidate.length === 0 && pendingAffectations > 0) {
      toast.error("Aucune affectation");
      return;
    }
    setValidating(true);
    try {
      const statementMeta = { filename: filename || "", source, file_hash: fileHash, storage_path: storagePath, total_lines: results?.length || 0, total_amount: round2(transactions.reduce((s, t) => s + (t.amount || 0), 0)), transactions, results };
      const res = await bankStatementAPI.validate({ assignments: toValidate, save_aliases: aliasesToSave, statement_meta: statementMeta, debits, draft_id: draftIdRef.current });
      const aliasMsg = aliasesToSave.length > 0 ? ` + ${aliasesToSave.length} alias memorise(s)` : "";
      if (toValidate.length === 0) {
        toast.success("Brouillon finalisé (aucun règlement à créer)");
      } else {
        toast.success(`${res.data.created} règlement(s) créé(s)${aliasMsg}`);
      }
      onDone(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setValidating(false); }
  };

  if (loading || !results) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin h-6 w-6 text-indigo-600 mr-2" /><span className="text-slate-500 text-sm">Rapprochement en cours...</span></div>;
  }

  const assignedCount = Object.keys(assignments).length;
  const totalPayments = Object.values(assignments).reduce((n, a) => n + (a.multi ? a.items.length : (a.document_id ? 1 : 0)), 0);
  const suggestedCount = results.filter(r => r.status === "suggested").length;
  const multiSuggestedCount = results.filter(r => r.status === "multi_suggested").length;
  const alreadyCount = results.filter(r => r.status === "deja_traite").length;
  const manuallySkippedCount = results.filter(r => r.status === "manually_skipped").length;
  // Hide manually-skipped lines from the wizard table but keep them counted.
  const visibleResults = results.filter(r => r.status !== "manually_skipped");
  const pendingSuggestions = visibleResults.filter(r => (r.status === "suggested" || r.status === "multi_suggested") && !assignments[r.index]).length;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card className="p-3"><p className="text-[10px] text-slate-500 uppercase">Lignes</p><p className="text-xl font-bold text-slate-900" data-testid="total-lines">{results.length}</p></Card>
        <Card className="p-3"><p className="text-[10px] text-indigo-500 uppercase">Suggestions</p><p className="text-xl font-bold text-indigo-600" data-testid="suggested-count">{suggestedCount + multiSuggestedCount}</p></Card>
        <Card className="p-3"><p className="text-[10px] text-emerald-500 uppercase">Validées</p><p className="text-xl font-bold text-emerald-600" data-testid="validated-count">{assignedCount}</p></Card>
        <Card className="p-3"><p className="text-[10px] text-slate-400 uppercase">Déjà traitées</p><p className="text-xl font-bold text-slate-400" data-testid="already-count">{alreadyCount}</p></Card>
        <Card className="p-3"><p className="text-[10px] text-slate-500 uppercase">Marquées manuel</p><p className="text-xl font-bold text-slate-500" data-testid="manually-skipped-count">{manuallySkippedCount}</p></Card>
        <Card className="p-3"><p className="text-[10px] text-amber-500 uppercase">À affecter</p><p className="text-xl font-bold text-amber-600" data-testid="unmatched-count">{visibleResults.filter(r => r.status === "unmatched").length}</p></Card>
      </div>

      {pendingSuggestions > 0 && (
        <div className="flex items-center justify-between p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
          <div className="flex items-center gap-2 text-sm text-indigo-700"><Sparkles size={15} /><span><strong>{pendingSuggestions}</strong> suggestion(s) en attente de validation</span></div>
          <Button onClick={acceptAllSuggestions} size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-7 px-3" data-testid="accept-all-btn"><CheckCheck size={13} className="mr-1" /> Tout valider</Button>
        </div>
      )}

      <Card className="border border-slate-200 rounded-lg bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-[10px] uppercase text-slate-500 w-8 px-2"></TableHead>
                <TableHead className="text-[10px] uppercase text-slate-500 px-2 w-20">Date</TableHead>
                <TableHead className="text-[10px] uppercase text-slate-500 px-2">Libellé opération</TableHead>
                <TableHead className="text-[10px] uppercase text-slate-500 px-2 text-right w-24">Montant</TableHead>
                <TableHead className="text-[10px] uppercase text-slate-500 px-2">Affectation</TableHead>
                <TableHead className="text-[10px] uppercase text-slate-500 px-2 w-20">Mode</TableHead>
                <TableHead className="text-[10px] uppercase text-slate-500 px-2 w-48">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleResults.map((r, i) => {
                const assigned = assignments[r.index];
                const isSuggested = r.status === "suggested" && !assigned;
                const isMultiSuggested = r.status === "multi_suggested" && !assigned;
                const isAlready = r.status === "deja_traite";
                return (
                  <TableRow key={r.label ? `${r.date}-${r.amount}-${i}` : `tx-${i}`} className={getBankRowClassName(assigned, isAlready, isSuggested, isMultiSuggested)} data-testid={`bank-row-${i}`}>
                    <TableCell className="px-2"><BankRowIcon assigned={assigned} isAlready={isAlready} isMultiSuggested={isMultiSuggested} isSuggested={isSuggested} /></TableCell>
                    <TableCell className="text-[11px] px-2 whitespace-nowrap">{r.date}</TableCell>
                    <TableCell className="px-2"><p className="text-[11px] text-slate-800 leading-tight" title={r.label}>{r.label}</p></TableCell>
                    <TableCell className="text-[11px] font-semibold text-right px-2 whitespace-nowrap">{formatEur(r.amount)}</TableCell>
                    <TableCell className="px-2">
                      {assigned ? (
                        <div className="text-[11px] leading-tight">
                          {assigned.multi ? (
                            <div><span className="font-medium text-indigo-600">{assigned.items.length} factures</span><span className="text-slate-400 ml-1">- {assigned.client_name}</span></div>
                          ) : (
                            <div><span className="font-mono font-medium text-indigo-600">{assigned.document_number}</span><span className="text-slate-400 ml-1">- {assigned.client_name}</span><Badge variant="outline" className="ml-1 text-[9px] px-1 py-0 text-emerald-600 border-emerald-300">OK</Badge></div>
                          )}
                        </div>
                      ) : isAlready ? (
                        <div className="text-[11px] leading-tight"><span className="text-slate-400 line-through">{(r.already_paid_refs || []).join(", ")}</span><Badge variant="outline" className="ml-1 text-[9px] px-1 py-0 text-slate-400 border-slate-300">Déjà traité</Badge></div>
                      ) : isMultiSuggested && r.multi_invoices ? (
                        <div className="text-[11px] leading-tight"><span className="font-mono font-medium text-purple-500">{r.multi_invoices.map(inv => inv.document_number).join(" + ")}</span><span className="text-slate-400 ml-1">- {r.multi_invoices[0]?.client_name}</span><Badge variant="outline" className="ml-1 text-[9px] px-1 py-0 text-purple-400 border-purple-200">Multi</Badge></div>
                      ) : isSuggested && r.best_match ? (
                        <div className="text-[11px] leading-tight"><span className="font-mono font-medium text-indigo-400">{r.best_match.document_number}</span><span className="text-slate-400 ml-1">- {r.best_match.client_name}</span><Badge variant="outline" className="ml-1 text-[9px] px-1 py-0 text-indigo-400 border-indigo-200">Suggestion</Badge></div>
                      ) : r.best_match ? (
                        // Status='unmatched' mais on a un best_match partiel (ex: nom client trouvé sans match montant exact).
                        // On affiche quand même la piste pour aider l'utilisateur (au lieu de "-").
                        <div className="text-[11px] leading-tight" title={(r.best_match.reasons || []).join(" • ")}>
                          <span className="font-mono font-medium text-amber-600">{r.best_match.document_number}</span>
                          <span className="text-slate-400 ml-1">- {r.best_match.client_name}</span>
                          <Badge variant="outline" className="ml-1 text-[9px] px-1 py-0 text-amber-600 border-amber-300">Hypothèse</Badge>
                        </div>
                      ) : (
                        <span className="text-[11px] text-slate-300">-</span>
                      )}
                    </TableCell>
                    <TableCell className="px-2">
                      {assigned && (
                        <div className="relative">
                          <select value={assigned.mode} onChange={e => setMode(r.index, e.target.value)} className="h-6 text-[10px] border border-slate-200 rounded px-1 pr-4 bg-white appearance-none cursor-pointer w-full" data-testid={`mode-${i}`}>
                            {MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                          </select>
                          <ChevronDown size={9} className="absolute right-0.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="px-2">
                      <div className="flex gap-1">
                        {assigned ? (
                          <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[10px] text-red-500" onClick={() => removeAssignment(r.index)} data-testid={`unlink-${i}`}>Retirer</Button>
                        ) : isAlready ? (
                          <span className="text-[10px] text-slate-300 italic">-</span>
                        ) : isMultiSuggested ? (
                          <>
                            <Button variant="default" size="sm" className="h-6 px-2 text-[10px] bg-purple-600 hover:bg-purple-700 text-white" onClick={() => acceptMultiSuggestion(r.index)} data-testid={`accept-multi-${i}`}><Layers size={10} className="mr-0.5" />Valider</Button>
                            <Button variant="outline" size="sm" className="h-6 px-1.5 text-[10px]" onClick={() => setManualDialog(r)} data-testid={`manual-${i}`}><Search size={10} className="mr-0.5" />Autre</Button>
                            <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[10px] text-slate-500 hover:text-slate-700 hover:bg-slate-100" onClick={() => markManuallyTreated(r.index)} title="Marquer cette ligne comme déjà traitée manuellement (aucun règlement créé)" data-testid={`mark-manual-${i}`}>Déjà traité</Button>
                          </>
                        ) : isSuggested ? (
                          <>
                            <Button variant="default" size="sm" className="h-6 px-2 text-[10px] bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => acceptSuggestion(r.index)} data-testid={`accept-${i}`}><Check size={10} className="mr-0.5" />Valider</Button>
                            <Button variant="outline" size="sm" className="h-6 px-1.5 text-[10px]" onClick={() => setManualDialog(r)} data-testid={`manual-${i}`}><Search size={10} className="mr-0.5" />Autre</Button>
                            <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[10px] text-slate-500 hover:text-slate-700 hover:bg-slate-100" onClick={() => markManuallyTreated(r.index)} title="Marquer cette ligne comme déjà traitée manuellement (aucun règlement créé)" data-testid={`mark-manual-${i}`}>Déjà traité</Button>
                          </>
                        ) : (
                          <>
                            {r.best_match && (
                              <Button variant="default" size="sm" className="h-6 px-2 text-[10px] bg-amber-500 hover:bg-amber-600 text-white"
                                onClick={() => acceptSuggestion(r.index)} title="Valider cette hypothèse (le montant ne match pas exactement, vérifiez avant)"
                                data-testid={`accept-hypothesis-${i}`}>
                                <Check size={10} className="mr-0.5" />Valider
                              </Button>
                            )}
                            <Button variant="outline" size="sm" className="h-6 px-1.5 text-[10px]" onClick={() => setManualDialog(r)} data-testid={`manual-${i}`}><Search size={10} className="mr-0.5" />Facture</Button>
                            <Button variant="outline" size="sm" className="h-6 px-1.5 text-[10px] text-indigo-600 border-indigo-300" onClick={() => setClientDialog(r)} data-testid={`client-alloc-${i}`}><Users size={10} className="mr-0.5" />Client</Button>
                            <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[10px] text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                              onClick={() => markManuallyTreated(r.index)}
                              title="Marquer cette ligne comme déjà traitée manuellement (en dehors de KomptAI). Aucun règlement ne sera créé."
                              data-testid={`mark-manual-${i}`}>
                              Déjà traité
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <Button variant="outline" onClick={onCancel} data-testid="cancel-btn"><ArrowLeft size={14} className="mr-1" /> Annuler</Button>
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-xs text-slate-500">{totalPayments} règlement(s) sur {assignedCount} ligne(s)</p>
          {(() => {
            // BUG UX A : let the user FINALIZE a draft where everything is
            // already handled (deja_traite + manually_skipped + 0 new
            // payments). The button stays enabled as long as every line has
            // a defined status (no remaining "À affecter"). A line counts as
            // handled when it has either a status in
            // (deja_traite | manually_skipped) OR a manual assignment in
            // ``assignments``. If there are no NEW payments to create, the
            // label switches to "Finaliser le brouillon".
            const pendingAffectations = visibleResults.filter(
              r => (r.status === "unmatched" || r.status === "suggested" || r.status === "multi_suggested")
                && !assignments[r.index]
            ).length;
            const hasNewPayments = totalPayments > 0;
            const canValidate = !validating && pendingAffectations === 0;
            const label = hasNewPayments
              ? `Enregistrer les règlements (${totalPayments})`
              : "Finaliser le brouillon";
            return (
              <Button onClick={validate} disabled={!canValidate}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-5"
                data-testid="validate-btn"
                title={pendingAffectations > 0
                  ? `${pendingAffectations} ligne(s) en attente d'action`
                  : (hasNewPayments ? "Crée les règlements et clôture le brouillon" : "Aucun règlement à créer, clôture juste le brouillon")
                }>
                {validating ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Check size={14} className="mr-1" />}
                {label}
              </Button>
            );
          })()}
        </div>
      </div>

      <ManualAssignDialog
        open={!!manualDialog}
        transaction={manualDialog}
        onAssign={(doc) => { if (manualDialog) { assignToDoc(manualDialog.index, doc); setManualDialog(null); } }}
        onMultiAssign={(items) => { if (manualDialog) { assignMultiple(manualDialog.index, items); setManualDialog(null); } }}
        onClose={() => setManualDialog(null)}
      />
      <ClientAllocDialog open={!!clientDialog} transaction={clientDialog} onAllocate={(items) => { if (clientDialog) { assignMultiple(clientDialog.index, items); setClientDialog(null); } }} onClose={() => setClientDialog(null)} />
    </div>
  );
}
